// src/composables/useNoteShare.js — v2
// Comportamento alterado: abre o popover sem gerar link automaticamente.
// O link só é gerado quando o usuário clicar em "Gerar link".

import { ref, computed } from "vue";
import { generateShareToken, revokeShareToken } from "@/services/noteService";
import { useNoteStore } from "@/stores/useNoteStore";
import { useNoteGeneralStore } from "@/stores/useNoteGeneralStore";

export function useNoteShare() {
  // --- Estado ---
  const isSharePopoverOpen = ref(false);
  const shareNoteId = ref(null);
  const shareUrl = ref("");
  const isGenerating = ref(false);
  const isCopied = ref(false);
  const shareError = ref("");

  // --- URL absoluta (prefixada com origin) ---
  const absoluteShareUrl = computed(() => {
    if (!shareUrl.value) return "";
    return `${window.location.origin}${shareUrl.value}`;
  });

  // --- Abre o popover  ---
    function openSharePopover(noteId, existingToken = null) {
    shareNoteId.value = noteId;
    shareUrl.value = existingToken ? `/shared/notes/${existingToken}` : "";
    shareError.value = "";
    isCopied.value = false;
    isSharePopoverOpen.value = true;
  }

  function _patchNoteShareToken(noteId, token) {
    // Atualiza em noteStore.notes (NoteList.vue / NoteCardModal.vue)
    const noteStore = useNoteStore();
    const noteInNotes = noteStore.notes.find(n => n.noteId === noteId);
    if (noteInNotes) noteInNotes.shareToken = token;
  
    const noteInAllNotes = noteStore.allNotes.find(n => n.noteId === noteId);
    if (noteInAllNotes) noteInAllNotes.shareToken = token;
  
    // Atualiza em noteGeneralStore.filteredNotes (NoteGeneralForm.vue)
    const noteGeneralStore = useNoteGeneralStore();
    const noteInGeneral = noteGeneralStore.filteredNotes?.find(n => n.noteId === noteId);
    if (noteInGeneral) noteInGeneral.shareToken = token;
  }

  // --- Gera o link ao clicar no botão "Gerar link" ---
  // DEPOIS:
async function generateLink(noteId) {
  if (isGenerating.value) return;
  shareError.value = "";
  isGenerating.value = true;
  try {
    const response = await generateShareToken(noteId);
    shareUrl.value = response.data.shareUrl;

    // >>> Atualiza shareToken na store para que a próxima abertura
    // do popover já saiba que há link ativo, sem precisar recarregar as notas.
    const token = response.data.shareToken;
    if (token) {
      _patchNoteShareToken(noteId, token);
    }
  } catch (err) {
    shareError.value =
      err.response?.data?.message ||
      "Erro ao gerar link de compartilhamento.";
  } finally {
    isGenerating.value = false;
  }
}

  function closeSharePopover() {
    isSharePopoverOpen.value = false;
    shareNoteId.value = null;
    shareUrl.value = "";
    shareError.value = "";
    isCopied.value = false;
  }

  // --- Copiar link para o clipboard ---
  async function copyLink() {
    if (!absoluteShareUrl.value) return;
    try {
      await navigator.clipboard.writeText(absoluteShareUrl.value);
      isCopied.value = true;
      setTimeout(() => {
        isCopied.value = false;
      }, 2500);
    } catch {
      const el = document.createElement("textarea");
      el.value = absoluteShareUrl.value;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      isCopied.value = true;
      setTimeout(() => {
        isCopied.value = false;
      }, 2500);
    }
  }

  // --- WhatsApp ---
  function shareWhatsApp() {
    if (!absoluteShareUrl.value) return;
    const encoded = encodeURIComponent(absoluteShareUrl.value);
    window.open(`https://wa.me/?text=${encoded}`, "_blank", "noopener");
  }

  // --- E-mail ---
  function shareEmail(noteTitle = "") {
    if (!absoluteShareUrl.value) return;
    const subject = encodeURIComponent(`Nota compartilhada: ${noteTitle}`);
    const body = encodeURIComponent(
      `Olá,\n\nCompartilho esta nota do Litterae com você:\n\n${absoluteShareUrl.value}`,
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  }

  // --- Web Share API (mobile nativo) ---
  async function shareNative(noteTitle = "") {
    if (!navigator.share || !absoluteShareUrl.value) return false;
    try {
      await navigator.share({
        title: noteTitle,
        text: "Confira esta nota no Litterae",
        url: absoluteShareUrl.value,
      });
      return true;
    } catch {
      return false;
    }
  }

  // --- Revogação ---
  const isRevoking = ref(false);

  async function revokeLink(noteId) {
    isRevoking.value = true;
    try {
      await revokeShareToken(noteId);
      shareUrl.value = "";
      closeSharePopover();
    } catch (err) {
      shareError.value = err.response?.data?.message || "Erro ao revogar link.";
    } finally {
      isRevoking.value = false;
    }
  }

  return {
    // estado
    isSharePopoverOpen,
    shareNoteId,
    absoluteShareUrl,
    isGenerating,
    isCopied,
    shareError,
    isRevoking,
    // ações
    openSharePopover,
    generateLink, // >>> NOVO: exposto para o popover chamar
    closeSharePopover,
    copyLink,
    shareWhatsApp,
    shareEmail,
    shareNative,
    revokeLink,
  };
}
